import API from '../Api/api';

const STORAGE_KEY = 'guest_user_activities';
const CLOSED_KEY = 'activity_timeline_closed';
const MAX_STORED = 20;
const TIMELINE_LIMIT = 3;

export const ACTIVITY_META = {
  search:   { icon: 'search',   tag: 'Search',   action: 'Continue' },
  wishlist: { icon: 'wishlist', tag: 'Wishlist', action: 'View' },
  booking:  { icon: 'booking',  tag: 'Booking',  action: 'View' },
  enquiry:  { icon: 'enquiry',  tag: 'Enquiry',  action: 'View' },
};

const safeParse = (raw, fallback) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const activityKey = (entry) => `${entry.type}:${entry.url || entry.property_id || entry.label}`;

const fixActivityUrl = (url, propertyId) => {
  if (url) {
    return String(url).replace(/^\/properties\/(\d+)(?:\/.*)?$/, '/property/$1');
  }
  if (propertyId) return `/property/${propertyId}`;
  return '/properties';
};

const normalizeActivity = (entry) => ({
  type: entry.type || 'search',
  label: entry.label || '',
  url: fixActivityUrl(entry.url, entry.property_id),
  property_id: entry.property_id,
  acted_at: entry.acted_at
    ? (typeof entry.acted_at === 'number' ? entry.acted_at : new Date(entry.acted_at).getTime())
    : Date.now(),
});

const sortByNewest = (rows) =>
  [...rows].sort((a, b) => (b.acted_at || 0) - (a.acted_at || 0));

const dedupeActivities = (rows) => {
  const seen = new Set();
  return rows.filter((row) => {
    const key = activityKey(row);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const buildSearchLabel = (filters = {}) => {
  const parts = [];
  if (filters.search) parts.push(`"${filters.search}"`);
  if (filters.listing_type && filters.listing_type !== 'all') {
    parts.push(filters.listing_type === 'rent' ? 'Rent' : 'Buy');
  }
  if (filters.listing_kind && filters.listing_kind !== 'all') {
    parts.push(filters.listing_kind === 'project' ? 'Projects' : 'Properties');
  }
  if (filters.city_name) parts.push(filters.city_name);
  return parts.length ? parts.join(' · ') : 'Property search';
};

export const buildSearchUrl = (filters = {}) => {
  const p = new URLSearchParams();
  if (filters.listing_kind && filters.listing_kind !== 'all') p.set('kind', filters.listing_kind);
  if (filters.listing_type && filters.listing_type !== 'all') p.set('type', filters.listing_type);
  if (filters.property_type && filters.property_type !== 'all') p.set('ptype', filters.property_type);
  if (filters.search) p.set('q', filters.search);
  if (filters.state_id) p.set('state', filters.state_id);
  if (filters.city_id) p.set('city', filters.city_id);
  if (filters.category_id?.length) p.set('cat', filters.category_id.join(','));
  if (filters.bhk?.length) p.set('bhk', filters.bhk.join(','));
  const qs = p.toString();
  return qs ? `/properties?${qs}` : '/properties';
};

export const getGuestActivities = () =>
  safeParse(localStorage.getItem(STORAGE_KEY), []).map(normalizeActivity);

const saveGuestActivity = (entry) => {
  if (!entry?.label || !entry?.type) return;
  const normalized = normalizeActivity(entry);
  const list = getGuestActivities().filter((a) => activityKey(a) !== activityKey(normalized));
  const next = [normalized, ...list].slice(0, MAX_STORED);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
};

export const trackActivity = async (entry, user) => {
  if (!entry?.label || !entry?.type) return;
  saveGuestActivity(entry);
  setTimelineClosed(false);
  window.dispatchEvent(new Event('user-activity-updated'));
  if (user?.id) {
    try {
      await API.post('/auth/track-activity', {
        type: entry.type,
        label: entry.label,
        url: entry.url,
        property_id: entry.property_id,
      });
    } catch { /* ignore */ }
  }
};

/** Backward-compatible search helper */
export const saveRecentSearch = async (entry, user) => {
  await trackActivity({
    type: 'search',
    label: entry.label,
    url: entry.url,
  }, user);
};

export const getRecentActivities = async (user, limit = TIMELINE_LIMIT) => {
  const takeLatest = (rows) => dedupeActivities(sortByNewest(rows)).slice(0, limit);

  if (user?.id) {
    try {
      const res = await API.get('/auth/user-activities');
      const rows = (res.data?.user_activities || []).map(normalizeActivity);
      if (rows.length) return takeLatest(rows);
    } catch {
      try {
        const res = await API.get('/auth/recent-searches');
        const legacy = (res.data?.user_activities || res.data?.recent_searches || []).map((item) => (
          item.type
            ? normalizeActivity(item)
            : normalizeActivity({
              type: 'search',
              label: item.label,
              url: item.url,
              acted_at: item.searched_at,
            })
        ));
        if (legacy.length) return takeLatest(legacy);
      } catch { /* fall through */ }
    }
  }
  return takeLatest(getGuestActivities());
};

export const isTimelineClosed = () => {
  try {
    return sessionStorage.getItem(CLOSED_KEY) === '1';
  } catch {
    return false;
  }
};

export const setTimelineClosed = (closed = true) => {
  try {
    sessionStorage.setItem(CLOSED_KEY, closed ? '1' : '0');
  } catch { /* ignore */ }
};

export const propertyActivityLabel = (property, prefix) => {
  const title = property?.location || property?.address || property?.title || `Property #${property?.id}`;
  return `${prefix}: ${title}`;
};
